import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import { User } from "./types/user";
import { useDeleteUserMutation, useGetUsersQuery } from "./api/usersApi";
import AddEditUser from "./components/AddEditUser";

const UserManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const navigate = useNavigate();

  const { data: users, isLoading, isError, isFetching } = useGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();

  const columns: Array<MRT_ColumnDef<User>> = [
    {
      header: "Name",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      id: "name",
      size: 100,
    },
    { header: "Email", accessorKey: "email", size: 150 },
  ];

  return (
    <Box>
      <DataTable<User>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Users</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${users?.data?.length ?? 0} Users`}
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedUser(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New User
            </Button>
          </Box>
        }
        list={users?.data ?? []}
        count={users?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(row);
                setOpenDialog(true);
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowNumberMode={undefined}
      />

      <AddEditUser open={openDialog} user={selectedUser} setOpen={setOpenDialog} />

      {selectedUser && (
        <ConfirmModal
          type="warning"
          title="Delete User"
          message={`Are you sure you want to delete this user '${selectedUser.firstName} ${selectedUser.lastName}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteUser(selectedUser.id.toString());
            setOpenConfirmModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </Box>
  );
};

export default UserManager;